/*
 * Suggestions Review panel — scans the whole document and reviews proposed
 * editorial changes one at a time, with "apply all of this rule". Ported from
 * handleScanDocSuggestions / handleSkip|Accept|AcceptAllRule in taskpane.js.
 * Office work is delegated to office/suggestions.ts via a session object.
 */

import { useState } from "react";
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import { resolvePlanAwareError } from "../../api/planErrors";
import { UpgradePlanButton } from "../common/UpgradePlanButton";
import { getFriendlyRule, resolveRuleSelection } from "../../rules/ruleLogic";
import {
  getDocumentAsBase64,
  readAllParagraphTexts,
  readSelectionText,
} from "../../office/document";
import {
  applyAllRuleSuggestions,
  applyDocSuggestion,
  syncHighlightDocSuggestion,
} from "../../office/suggestions";
import {
  getCommentAnchorText,
  getCommentBody,
  insertDocComment,
  insertSelectionComment,
  isDocComment,
  isDocSuggestion,
  normalizeReviewFindings,
  occurrenceIndexBefore,
  syncHighlightDocComment,
} from "../../office/commentFindings";
import { replaceOccurrenceInSelection } from "../../office/selection";
import { scanSuggestions, scanTextSuggestions } from "../../api/client";
import { CollapsibleSection } from "../common/CollapsibleSection";
import { SuggestionCard } from "../common/SuggestionCard";
import { useFindingsAutoCollapse } from "../common/useFindingsAutoCollapse";
import { hasSuggestionFindings } from "../../state/findings";
import type { DocReviewFinding, DocSuggestion, DocSuggestionSession } from "../../state/types";

const useStyles = makeStyles({
  panel: { display: "flex", flexDirection: "column", gap: "12px" },
  actions: { display: "flex", flexWrap: "wrap", gap: "8px" },
});

function buildSession(): DocSuggestionSession {
  const s = useStore.getState();
  return {
    docSuggestions: s.docSuggestions,
    currentDocSuggestionIndex: s.currentDocSuggestionIndex,
    originalParagraphTexts: s.originalParagraphTexts,
    appliedChangesInParagraph: s.appliedChangesInParagraph,
  };
}

/*
 * Comment-only findings share the review queue with replacements, but accepting
 * them inserts a Word comment while replacement findings keep the existing flow.
 */
async function syncCurrentDocFinding(): Promise<void> {
  const s = useStore.getState();
  const finding = s.docSuggestions[s.currentDocSuggestionIndex];
  if (!finding) return;
  if (isDocComment(finding)) {
    await syncHighlightDocComment(finding, s.originalParagraphTexts);
    return;
  }
  await syncHighlightDocSuggestion(buildSession());
}

function findingSentence(finding: DocReviewFinding | null): string {
  if (!finding) return "";
  if (isDocComment(finding)) {
    return String(finding.sentenceBefore || finding.paragraphText || finding.contextText || "");
  }
  return String(finding.sentenceBefore || "");
}

function findingBefore(finding: DocReviewFinding | null): string {
  if (!finding) return "";
  return isDocComment(finding) ? getCommentAnchorText(finding) : String(finding.before ?? "");
}

function findingAfter(finding: DocReviewFinding | null): string {
  if (!finding) return "";
  return isDocComment(finding) ? getCommentBody(finding) : String(finding.after ?? "");
}

export function SuggestionsPanel(): JSX.Element {
  const styles = useStyles();
  const { t, lang, format } = useTranslation();
  const patch = useStore((s) => s.patch);
  const setLoading = useStore((s) => s.setLoading);
  const rules = useStore((s) => s.rules);
  const catalogSections = useStore((s) => s.catalogSections);
  const token = useStore((s) => s.token);
  const selectedRuleIds = useStore((s) => s.selectedRuleIds);
  const preferences = useStore((s) => s.preferences);
  const docSuggestions = useStore((s) => s.docSuggestions);
  const currentIndex = useStore((s) => s.currentDocSuggestionIndex);
  const selectionSuggestions = useStore((s) => s.reviewSelectionSuggestions);
  const currentSelectionIndex = useStore((s) => s.currentReviewSelectionIndex);
  const selectionWorkingText = useStore((s) => s.reviewSelectionWorkingText);
  const selectionShift = useStore((s) => s.reviewSelectionShift);

  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const ruleIds = resolveRuleSelection(rules, selectedRuleIds);

  const presentError = (error: unknown) => {
    const presentation = resolvePlanAwareError(error, {
      quota: t.quotaExceededMessage,
      forbidden: t.serviceForbiddenMessage,
      fallback: t.statusError,
    });
    setIsError(true);
    setShowUpgrade(presentation.showUpgrade);
    setMessage(`${t.statusError} ${presentation.message}`);
  };

  const scan = async () => {
    setScanning(true);
    setMessage("");
    setIsError(false);
    setShowUpgrade(false);
    try {
      patch({ reviewSelectionSuggestions: [], currentReviewSelectionIndex: -1 });

      const originalParagraphTexts = await readAllParagraphTexts();
      patch({ originalParagraphTexts, appliedChangesInParagraph: {} });

      const base64Doc = await getDocumentAsBase64();
      const data = await scanSuggestions(token, base64Doc, ruleIds, { ...preferences });
      const changes = (data?.changes || []).filter(
        (finding): finding is DocSuggestion & { findingType: "change" } => !isDocComment(finding)
      );
      const findings = normalizeReviewFindings(changes, data?.comments || []);

      if (findings.length === 0) {
        setMessage(t.noSuggestions);
        patch({ docSuggestions: [], currentDocSuggestionIndex: -1 });
      } else {
        patch({ docSuggestions: findings, currentDocSuggestionIndex: 0 });
        await syncCurrentDocFinding();
      }
    } catch (err) {
      presentError(err);
    } finally {
      setScanning(false);
    }
  };

  const scanSelection = async () => {
    setScanning(true);
    setMessage("");
    setIsError(false);
    setShowUpgrade(false);
    try {
      patch({ docSuggestions: [], currentDocSuggestionIndex: -1 });

      const selectedText = await readSelectionText();
      if (!selectedText?.trim()) throw new Error(t.emptySelection);

      const data = await scanTextSuggestions(token, selectedText, ruleIds, { ...preferences });
      const changes = (data?.changes || []) as DocSuggestion[];
      const findings = normalizeReviewFindings(changes, data?.comments || []);

      if (findings.length === 0) {
        setMessage(t.noSuggestions);
        patch({ reviewSelectionSuggestions: [], currentReviewSelectionIndex: -1 });
      } else {
        patch({
          reviewSelectionOriginalText: selectedText,
          reviewSelectionWorkingText: selectedText,
          reviewSelectionSuggestions: findings,
          currentReviewSelectionIndex: 0,
          reviewSelectionShift: 0,
        });
      }
    } catch (err) {
      presentError(err);
    } finally {
      setScanning(false);
    }
  };

  const skip = async () => {
    patch({ currentDocSuggestionIndex: currentIndex + 1 });
    await syncCurrentDocFinding();
  };

  const skipSelectionSuggestion = () =>
    patch({ currentReviewSelectionIndex: currentSelectionIndex + 1 });

  const handleCancel = () => {
    patch({ docSuggestions: [], currentDocSuggestionIndex: -1 });
    setMessage("");
    setIsError(false);
  };

  const handleCancelSelection = () => {
    patch({ reviewSelectionSuggestions: [], currentReviewSelectionIndex: -1 });
    setMessage("");
    setIsError(false);
  };

  const accept = async () => {
    setBusy(true);
    setLoading(true);
    try {
      const finding = docSuggestions[currentIndex];
      if (isDocComment(finding)) {
        await insertDocComment(
          finding,
          useStore.getState().originalParagraphTexts,
          t.commentUnsupported
        );
        patch({ currentDocSuggestionIndex: currentIndex + 1 });
      } else {
        const session = buildSession();
        await applyDocSuggestion(session);
        patch({
          appliedChangesInParagraph: session.appliedChangesInParagraph,
          currentDocSuggestionIndex: currentIndex + 1,
        });
      }
      await syncCurrentDocFinding();
    } catch (err) {
      presentError(err);
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };

  const acceptSelectionSuggestion = async () => {
    const change = selectionSuggestions[currentSelectionIndex];
    if (!change) return;
    setBusy(true);
    try {
      if (isDocComment(change)) {
        await insertSelectionComment(
          change,
          useStore.getState().reviewSelectionOriginalText,
          t.commentUnsupported
        );
        patch({ currentReviewSelectionIndex: currentSelectionIndex + 1 });
        return;
      }

      const before = String(change.before ?? "");
      const after = String(change.after ?? "");
      const start = (change.position ?? 0) + selectionShift;
      const end = start + before.length;
      const newWorking = selectionWorkingText.slice(0, start) + after + selectionWorkingText.slice(end);
      const occurrenceIndex = occurrenceIndexBefore(selectionWorkingText, before, start);

      await replaceOccurrenceInSelection(before, after, occurrenceIndex, t.occurrenceNotFound);

      patch({
        reviewSelectionWorkingText: newWorking,
        reviewSelectionShift: selectionShift + after.length - before.length,
        currentReviewSelectionIndex: currentSelectionIndex + 1,
      });
    } catch (err) {
      presentError(err);
    } finally {
      setBusy(false);
    }
  };

  const acceptAllRule = async () => {
    const change = docSuggestions[currentIndex];
    if (!change || !isDocSuggestion(change)) return;
    setBusy(true);
    setLoading(true);
    try {
      const session = buildSession();
      await applyAllRuleSuggestions(session, change.ruleId);
      patch({
        docSuggestions: session.docSuggestions,
        appliedChangesInParagraph: session.appliedChangesInParagraph,
      });
      await syncCurrentDocFinding();
    } catch (err) {
      presentError(err);
    } finally {
      setLoading(false);
      setBusy(false);
    }
  };

  const openReport = () => patch({ reportModalOpen: true, reportModalTarget: "suggestions" });
  const openReportSelection = () =>
    patch({ reportModalOpen: true, reportModalTarget: "review-selection" });

  const hasActive = currentIndex >= 0 && currentIndex < docSuggestions.length;
  const change = hasActive ? docSuggestions[currentIndex] : null;
  const friendly = change ? getFriendlyRule(change.ruleId, rules, catalogSections, lang) : null;
  const sameRuleRemaining = change
    ? docSuggestions
        .slice(currentIndex)
        .filter((s) => isDocSuggestion(s) && s.ruleId === change.ruleId).length
    : 0;
  const reviewedAll = docSuggestions.length > 0 && currentIndex >= docSuggestions.length;

  const hasSelectionActive =
    currentSelectionIndex >= 0 && currentSelectionIndex < selectionSuggestions.length;
  const selectionChange = hasSelectionActive ? selectionSuggestions[currentSelectionIndex] : null;
  const selectionFriendly = selectionChange
    ? getFriendlyRule(selectionChange.ruleId, rules, catalogSections, lang)
    : null;
  const selectionReviewedAll =
    selectionSuggestions.length > 0 && currentSelectionIndex >= selectionSuggestions.length;

  const hasFindings = useStore(hasSuggestionFindings);
  const { controlsExpanded, toggleControls, findingsRef } = useFindingsAutoCollapse(hasFindings);

  const noActiveSession =
    !hasActive &&
    !hasSelectionActive &&
    docSuggestions.length === 0 &&
    selectionSuggestions.length === 0;

  return (
    <div className="panel-section">
      <CollapsibleSection
        title={t.scanSuggestionsBtn}
        expanded={controlsExpanded}
        onToggle={toggleControls}
      >
        <MessageBar intent="info" className="status-card">
          <MessageBarBody>{t.suggestionsInfo}</MessageBarBody>
        </MessageBar>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            onClick={scan}
            disabled={scanning || busy || ruleIds.length === 0}
          >
            {t.scanSuggestionsBtn}
          </Button>
          <Button
            appearance="secondary"
            onClick={scanSelection}
            disabled={scanning || busy || ruleIds.length === 0}
          >
            {t.scanSelectionSuggestionsBtn}
          </Button>
        </div>
      </CollapsibleSection>

      {scanning ? <Spinner size="small" label={t.statusReading} /> : null}

      {message ? (
        isError ? (
          <MessageBar intent="error">
            <MessageBarBody>
              {message}
              {showUpgrade ? <UpgradePlanButton label={t.quotaExceededUpgrade} /> : null}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <Text>{message}</Text>
        )
      ) : null}

      {(reviewedAll || selectionReviewedAll) && !message ? <Text>{t.noSuggestions}</Text> : null}

      {noActiveSession && !scanning && !message ? <Text>{t.promptSuggestions}</Text> : null}

      <div ref={findingsRef}>
        {change && friendly ? (
          <SuggestionCard
            counterText={format(t.suggestionsCounter, {
              current: currentIndex + 1,
              total: docSuggestions.length,
            })}
            progressPercent={((currentIndex + 1) / docSuggestions.length) * 100}
            sentence={findingSentence(change)}
            before={findingBefore(change)}
            after={findingAfter(change)}
            ruleName={friendly.name}
            ruleTooltip={change.explanation || friendly.desc}
            onAccept={accept}
            acceptLabel={isDocComment(change) ? t.addCommentBtn : undefined}
            onSkip={skip}
            onCancel={handleCancel}
            onAcceptAllRule={isDocSuggestion(change) ? acceptAllRule : undefined}
            acceptAllRuleLabel={`${t.acceptAllRuleBtn} (${sameRuleRemaining})`}
            onReport={isDocSuggestion(change) ? openReport : undefined}
            busy={busy}
          />
        ) : null}

        {selectionChange && selectionFriendly ? (
          <SuggestionCard
            counterText={format(t.suggestionsCounter, {
              current: currentSelectionIndex + 1,
              total: selectionSuggestions.length,
            })}
            progressPercent={((currentSelectionIndex + 1) / selectionSuggestions.length) * 100}
            sentence={findingSentence(selectionChange)}
            before={findingBefore(selectionChange)}
            after={findingAfter(selectionChange)}
            ruleName={selectionFriendly.name}
            ruleTooltip={selectionChange.explanation || selectionFriendly.desc}
            onAccept={acceptSelectionSuggestion}
            acceptLabel={isDocComment(selectionChange) ? t.addCommentBtn : undefined}
            onSkip={skipSelectionSuggestion}
            onCancel={handleCancelSelection}
            onReport={isDocSuggestion(selectionChange) ? openReportSelection : undefined}
            busy={busy}
          />
        ) : null}
      </div>
    </div>
  );
}
